import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskText, Reveal } from "./Reveal";
import { PAYMENT, IMG } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Wholesale() {
  const [form, setForm] = useState({ name: "", email: "", company: "", products: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/wholesale`, form);
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="wholesale" className="py-24 sm:py-40 bg-[var(--bone-2)]" data-testid="section-wholesale">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="overline text-[var(--terra)] mb-6">Chapter 08 — Partners</p>
          <MaskText
            as="h2"
            lines={["Wholesale", "& MOQ."]}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.88] tracking-tighter"
          />
          <Reveal delay={0.2}>
            <p className="font-body text-lg text-[var(--ink-soft)] leading-relaxed mt-8 max-w-md">
              Buying in volume? We offer wholesale pricing on peptides and skin
              actives for verified partners with a minimum order quantity. Tell us
              what you need and we'll send a quote.
            </p>
            <p className="font-body text-base text-[var(--terra)] leading-relaxed mt-4 max-w-md" data-testid="wholesale-500-note">
              Wholesale orders over $500 are deeply discounted — the deeper pricing applies only on the amount over $500.
            </p>
            <Link to="/wholesale-order" className="btn-ink inline-flex items-center gap-2 px-7 py-4 rounded-full mt-8 hover:scale-[1.03] transition-transform" data-testid="order-wholesale-now-btn">
              Order Wholesale Now! <ArrowRight size={16} />
            </Link>
            <a href={`mailto:${PAYMENT.wholesaleEmail}`} className="block overline text-[var(--ink)] border-b border-[var(--ink)] pb-1 mt-6 w-fit" data-testid="wholesale-email-link">
              {PAYMENT.wholesaleEmail}
            </a>
            <div className="frame aspect-[16/10] mt-10 border border-[var(--line)]">
              <img src={IMG.kit} alt="Balance wholesale kit" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {sent ? (
            <div className="border border-[var(--line)] bg-[var(--bone)] p-10 text-center" data-testid="wholesale-success">
              <h3 className="font-display text-4xl">Inquiry received.</h3>
              <p className="font-body text-[var(--ink-soft)] mt-4">
                Thanks {form.name.split(" ")[0] || "there"} — our partnerships team will reach out with wholesale pricing shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-[var(--line)] bg-[var(--bone)] p-8 sm:p-10 space-y-6" data-testid="wholesale-form">
              {[
                { k: "name", l: "Name", t: "text", req: true },
                { k: "email", l: "Business email", t: "email", req: true },
                { k: "company", l: "Company (optional)", t: "text" },
                { k: "products", l: "Products of interest", t: "text" },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="overline text-[var(--ink-soft)]">{f.l}</span>
                  <input
                    type={f.t}
                    required={f.req}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 font-body text-lg"
                    data-testid={`wholesale-${f.k}`}
                  />
                </label>
              ))}
              <label className="block">
                <span className="overline text-[var(--ink-soft)]">Estimated volume / message</span>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-[var(--line)] focus:border-[var(--ink)] outline-none py-3 font-body text-lg resize-none"
                  data-testid="wholesale-message"
                />
              </label>
              <button type="submit" disabled={!valid || busy} className="btn-ink w-full py-4 rounded-full disabled:opacity-40" data-testid="wholesale-submit">
                {busy ? "Sending…" : "Request wholesale quote"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
